import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {Config, DataType, Endianness, RegType} from 'smag-commons/cfg/connector/modbus';
import {BehaviorSubject, forkJoin, merge, Observable, of, Subject, Subscription} from 'rxjs';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import * as _ from 'lodash';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {isBlank, isDefined, keySorter} from '../../../commons/commons';
import {
    validateGt,
    validateInteger,
    validateIntegerArray,
    validateNotBlank,
    validateNotBlankTrim,
    validateNotDuplicate
} from '../../../commons/validators';
import {
    DEFAULT_POLLING_INTERVAL,
    EDITOR_BUILTIN_FUNCTIONS,
    EDITOR_BUILTIN_TYPES,
    MODBUS_DATA_TYPES,
    MODBUS_DEFAULT_DEVICE_ADDRESS,
    MODBUS_DEFAULT_DEVICE_DATA_TYPE,
    MODBUS_DEFAULT_DEVICE_ENDIANNESS,
    MODBUS_DEFAULT_DEVICE_REGISTER_TYPE,
    MODBUS_DEFAULT_GATEWAY_FRAME_TYPE,
    MODBUS_DEFAULT_GATEWAY_PORT,
    MODBUS_DEFAULT_GATEWAY_RETRY_ON_EMPTY,
    MODBUS_FRAME_TYPES,
    MODBUS_REG_TYPES,
    MODBUS_TEMPLATES,
    MODBUS_WORD_TYPES,
    ModbusTemplate
} from '../connectors';
import {
    PollingPolicy,
    PollingPolicyComponent,
    PollingPolicyData,
    setDevicePolicy
} from '../polling-policy/polling-policy.component';
import {mkDeviceParamValue} from '../../../shared/entities';
import {Features} from '../expression-editor/expression-editor.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';

interface GatewayConfig {
    gatewayId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface DeviceConfig {
    devId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface PagedGateways {
    data: GatewayConfig[];
    total: number;
}

interface PagedDevices {
    data: DeviceConfig[];
    total: number;
}

const VARIABLE_ACCESSORS = ['r', 'v']; // "registers", "variable" (command)

const GATEWAY_ADD_DIALOG_MAX_WIDTH = 500;
const GATEWAY_ADD_FROM_TEMPLATE_DIALOG_MAX_WIDTH = 500;
const GATEWAY_RENAME_DIALOG_MAX_WIDTH = 500;
const GATEWAY_DELETE_DIALOG_MAX_WIDTH = 500;
const GATEWAY_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADD_DIALOG_MAX_WIDTH = 500;
const DEVICE_RENAME_DIALOG_MAX_WIDTH = 500;
const DEVICE_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15, 30, 50];

const EXPRESSION_HELP = 'Input registers are available through register-value map "r". Command value is supplied in variable "v".__{modbus}';

@Component({
    selector: 'app-modbus-config',
    templateUrl: './modbus-config.component.html',
    styleUrls: ['./modbus-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Items per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ModbusConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadDevsPages: Subject<void>;
    private reloadGwsPages: Subject<void>;
    @ViewChild('addGateway') private addGatewayDialog: TemplateRef<any>;
    @ViewChild('addGatewayFromTemplate') private addGatewayFromTemplateDialog: TemplateRef<any>;
    @ViewChild('renameGateway') private renameGatewayDialog: TemplateRef<any>;
    @ViewChild('deleteGateway') private deleteGatewayDialog: TemplateRef<any>;
    @ViewChild('advancedGatewaySettings') private advancedGatewaySettingsDialog: TemplateRef<any>;
    @ViewChild('addDevice') private addDeviceDialog: TemplateRef<any>;
    @ViewChild('renameDevice') private renameDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteDevice') private deleteDeviceDialog: TemplateRef<any>;
    @ViewChild('advancedDeviceSettings') private advancedDeviceSettingsDialog: TemplateRef<any>;
    @ViewChild('devsPaginator') private devsPaginator: MatPaginator;
    @ViewChild('gwsPaginator') private gwsPaginator: MatPaginator;

    pagedDevs: BehaviorSubject<PagedDevices>;
    pagedGws: BehaviorSubject<PagedGateways>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    form: FormGroup;
    gateways: GatewayConfig[];
    devices: DeviceConfig[];

    // exported in HTML
    MODBUS_TEMPLATES = MODBUS_TEMPLATES;
    MODBUS_FRAME_TYPES = MODBUS_FRAME_TYPES;
    MODBUS_DATA_TYPES = MODBUS_DATA_TYPES;
    MODBUS_REG_TYPES = MODBUS_REG_TYPES;
    MODBUS_WORD_TYPES = MODBUS_WORD_TYPES;
    EXPRESSION_HELP = EXPRESSION_HELP;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private fb: FormBuilder,
                private readonly dialog: MatDialog,
                private ngZone: NgZone,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.reloadDevsPages = new Subject<void>();
        this.reloadGwsPages = new Subject<void>();
        this.pagedDevs = new BehaviorSubject<PagedDevices>(null);
        this.pagedGws = new BehaviorSubject<PagedGateways>(null);
    }

    ngOnInit() {
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({});
        this.gateways = Object.keys(this.cfg.gateways).map(gatewayId => this.mkGatewayConfig(gatewayId));
        this.gateways.sort(keySorter('gatewayId'));
        this.devices = Object.keys(this.cfg.devices).map(devId => this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();
    }

    ngAfterViewInit() {
        merge(this.gwsPaginator.page,
            this.reloadGwsPages.pipe(tap(() => this.gwsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedGws(this.gwsPaginator.pageSize, this.gwsPaginator.pageIndex).pipe(
                    startWith(null as PagedGateways) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedGws => {
            if (pagedGws) {
                this.pagedGws.next(pagedGws);
                this.changeDetectorRef.detectChanges();
            }
        });

        merge(this.devsPaginator.page,
            this.reloadDevsPages.pipe(tap(() => this.devsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedDevices(this.devsPaginator.pageSize, this.devsPaginator.pageIndex).pipe(
                    startWith(null as PagedDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedDevs => {
            if (pagedDevs) {
                this.pagedDevs.next(pagedDevs);
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByGatewayId(index: number, gatewayCfg: GatewayConfig): string {
        return gatewayCfg.gatewayId;
    }

    trackByDevId(index: number, devCfg: DeviceConfig): string {
        return devCfg.devId;
    }

    onClickAddGateway() {
        const gateways = this.gateways.map(gateway => gateway.gatewayId);
        this.dialog.open(this.addGatewayDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank, validateNotDuplicate(gateways)])
            },
            maxWidth: `${GATEWAY_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addGateway(id);
            }
        });
    }

    onClickAddGatewayFromTemplate() {
        const form = this.fb.group({
            template: new FormControl(null, [Validators.required]),
        });
        form.addControl('id', new FormControl('',
            [validateNotBlank],
            [this.mkTemplateGatewayIdValidator(() => form.controls.template.value)]));
        // template changes have an impact on id's validity
        const templateSub = form.controls.template.valueChanges.subscribe(() => form.controls.id.updateValueAndValidity());
        this.dialog.open(this.addGatewayFromTemplateDialog, {
            data: {
                form,
                deviceNamesGenerator: () => {
                    const template = form.controls.template.value;
                    const gatewayId = form.controls.id.value;
                    return template && gatewayId && gatewayId.trim().length ? getTemplateDeviceNames(gatewayId, template) : [];
                }
            },
            maxWidth: `${GATEWAY_ADD_FROM_TEMPLATE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(add => {
            templateSub.unsubscribe();
            if (add) {
                this.addGatewayFromTemplate(form.controls.id.value, form.controls.template.value);
            }
        });
    }

    onClickRenameGateway(gatewayId: string) {
        const gateways = this.gateways.map(gateway => gateway.gatewayId).filter(id => id !== gatewayId);
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].gateway === gatewayId);
        this.dialog.open(this.renameGatewayDialog, {
            data: {
                gatewayId,
                connectedDevs: connectedDevs.map(dev => dev.devId),
                idFormControl: new FormControl(gatewayId, [validateNotBlank, validateNotDuplicate(gateways)])
            },
            maxWidth: `${GATEWAY_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== gatewayId) {
                this.renameGateway(gatewayId, newName, connectedDevs);
            }
        });
    }

    onClickDeleteGateway(gatewayId: string) {
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].gateway === gatewayId);
        this.dialog.open(this.deleteGatewayDialog, {
            data: {
                gatewayId,
                connectedDevs: connectedDevs.map(dev => dev.devId)
            },
            maxWidth: `${GATEWAY_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteGateway(gatewayId, connectedDevs);
            }
        });
    }

    onClickGatewayAdvancedSettings(gateway: GatewayConfig) {
        const form = this.fb.group({
            timeout: new FormControl(this.cfg.gateways[gateway.gatewayId].timeout, [validateInteger, validateGt(0)]),
            retries: new FormControl(this.cfg.gateways[gateway.gatewayId].retries, [validateInteger, Validators.min(0)]),
            retryOnEmpty: new FormControl(!!this.cfg.gateways[gateway.gatewayId].retry_on_empty, [])
        });
        this.dialog.open(this.advancedGatewaySettingsDialog, {
            data: {
                gatewayId: gateway.gatewayId,
                form
            },
            maxWidth: `${GATEWAY_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setGatewayAdvancedSettings(gateway,
                    form.controls.timeout.value,
                    form.controls.retries.value,
                    form.controls.retryOnEmpty.value);
            }
        });
    }

    onClickAddDevice() {
        this.dialog.open(this.addDeviceDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank], [this.mkDeviceUniquenessValidator()])
            },
            maxWidth: `${DEVICE_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addDevice(id);
            }
        });
    }

    onClickRenameDevice(devId: string) {
        this.dialog.open(this.renameDeviceDialog, {
            data: {
                devId,
                idFormControl: new FormControl(devId, [validateNotBlank], [this.mkDeviceUniquenessValidator(devId)])
            },
            maxWidth: `${DEVICE_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== devId) {
                this.renameDevice(devId, newName);
            }
        });
    }

    onClickDeleteDevice(devId: string) {
        this.dialog.open(this.deleteDeviceDialog, {
            data: {
                devId
            },
            maxWidth: `${DEVICE_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteDevice(devId);
            }
        });
    }

    onClickDevicePollingPolicy(devId: string) {
        this.dialog.open(PollingPolicyComponent, {
            data: {
                devId,
                dev: this.cfg.devices[devId]
            } as PollingPolicyData,
            maxWidth: `${DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((policy: PollingPolicy) => {
            if (policy) {
                this.setDevicePolicy(devId, policy);
            }
        });
    }

    onClickDeviceAdvancedSettings(dev: DeviceConfig) {
        const register = this.cfg.devices[dev.devId].register;
        const registers = _.isArray(register) ? register : (!!register ? [register] : []);
        const form = this.fb.group({
            address: new FormControl(this.cfg.devices[dev.devId].address, [validateInteger, Validators.min(1)]),
            dataType: new FormControl(this.cfg.devices[dev.devId].data_type, [Validators.required]),
            regType: new FormControl(this.cfg.devices[dev.devId].reg_type, [Validators.required]),
            word: new FormControl(this.cfg.devices[dev.devId].word, [Validators.required]),
            expression: new FormControl(this.cfg.devices[dev.devId].expression, [
                (ctrl: AbstractControl) => this.validateExpressionAgainstRegister(ctrl, dev)
            ])
        });
        this.dialog.open(this.advancedDeviceSettingsDialog, {
            data: {
                devId: dev.devId,
                form,
                registers,
                features: this.mkEditorFeatures(registers.map(r => `${r}`))
            },
            maxWidth: `${DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setDeviceAdvancedSettings(dev,
                    form.controls.address.value,
                    form.controls.dataType.value,
                    form.controls.regType.value,
                    form.controls.word.value,
                    form.controls.expression.value);
            }
        });
    }

    private get isValid(): boolean {
        return this.form.valid &&
            this.gateways.every(gateway => gateway.form.valid) &&
            this.devices.every(dev => dev.form.valid);
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private mkEditorFeatures(registers: string[]): Features {
        const cfg = this.appCfg.exprEditor;
        return {
            keywords: _.fromPairs([
                ...EDITOR_BUILTIN_TYPES.map(t => [t, {color: cfg.builtinTypesColor, bold: cfg.builtinsBold}]),
                ...EDITOR_BUILTIN_FUNCTIONS.map(f => [f, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}]),
                ...VARIABLE_ACCESSORS.map(f => [f, {
                    color: cfg.variableAccessorsColor,
                    bold: cfg.variableAccessorBold
                }]),
                ...registers.map(m => [m, {color: cfg.variablesColor, bold: cfg.variablesBold}])
            ])
        };
    }

    private addGateway(gatewayId: string) {
        // config must be updated in order for mkGatewayConfig() to work
        this.cfg.gateways[gatewayId] = {
            host: null,
            port: MODBUS_DEFAULT_GATEWAY_PORT,
            frame: MODBUS_DEFAULT_GATEWAY_FRAME_TYPE,
            retry_on_empty: MODBUS_DEFAULT_GATEWAY_RETRY_ON_EMPTY
        };
        this.gateways.push(this.mkGatewayConfig(gatewayId));
        this.gateways.sort(keySorter('gatewayId'));
        this.reloadGwsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private addGatewayFromTemplate(gatewayId: string, template: ModbusTemplate) {
        // add gateway
        // config must be updated in order for mkGatewayConfig() to work
        this.cfg.gateways[gatewayId] = {
            host: null,
            port: MODBUS_DEFAULT_GATEWAY_PORT,
            frame: template.gateway.frame || MODBUS_DEFAULT_GATEWAY_FRAME_TYPE,
            retry_on_empty: MODBUS_DEFAULT_GATEWAY_RETRY_ON_EMPTY
        };
        this.gateways.push(this.mkGatewayConfig(gatewayId));
        this.gateways.sort(keySorter('gatewayId'));
        // add devices
        template.devices.forEach(dev => {
            const devId = mkDeviceParamValue(gatewayId, dev.suffix);
            // config must be updated in order for mkDeviceConfig() to work
            this.cfg.devices[devId] = {
                gateway: gatewayId,
                uom: dev.uom,
                address: dev.address,
                register: dev.register,
                data_type: dev.dataType,
                reg_type: dev.regType,
                word: dev.word,
                interval: DEFAULT_POLLING_INTERVAL,
                expression: dev.expression
            };
            this.devices.push(this.mkDeviceConfig(devId));
        });
        this.devices.sort(keySorter('devId'));
        this.reloadGwsPages.next();
        this.reloadDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameGateway(gatewayId: string, newName: string, connectedDevs: DeviceConfig[]) {
        const gateway = this.gateways.find(gateway => gateway.gatewayId === gatewayId);
        gateway.gatewayId = newName;
        this.cfg.gateways[newName] = this.cfg.gateways[gatewayId];
        delete this.cfg.gateways[gatewayId];
        this.gateways.sort(keySorter('gatewayId'));
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.gatewayId.setValue(newName, {emitEvent: false});
            this.cfg.devices[dev.devId].gateway = newName;
        });
        this.reloadGwsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteGateway(gatewayId: string, connectedDevs: DeviceConfig[]) {
        const idx = this.gateways.findIndex(gateway => gateway.gatewayId === gatewayId);
        this.gateways[idx].valueSubscription.unsubscribe();
        this.gateways[idx].statusSubscription.unsubscribe();
        this.gateways.splice(idx, 1);
        delete this.cfg.gateways[gatewayId];
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.gatewayId.setValue(null, {emitEvent: false});
            dev.form.controls.gatewayId.markAsTouched();
            this.cfg.devices[dev.devId].gateway = null;
        });
        this.reloadGwsPages.next();
        this.updateConfig();
        // validity may change if there are some affected devices
        if (connectedDevs.length) {
            this.updateValidity();
        }
    }

    private setGatewayAdvancedSettings(gateway: GatewayConfig,
                                       timeout: number,
                                       retries: number,
                                       retryOnEmpty: boolean) {
        this.cfg.gateways[gateway.gatewayId].timeout = timeout;
        this.cfg.gateways[gateway.gatewayId].retries = retries;
        this.cfg.gateways[gateway.gatewayId].retry_on_empty = retryOnEmpty;
        this.updateConfig();
        // validity has not changed, as advanced settings form has already been validated
    }

    private addDevice(devId: string) {
        // config must be updated in order for mkDeviceConfig() to work
        this.cfg.devices[devId] = {
            gateway: null,
            register: null,
            interval: DEFAULT_POLLING_INTERVAL,
            address: MODBUS_DEFAULT_DEVICE_ADDRESS,
            data_type: MODBUS_DEFAULT_DEVICE_DATA_TYPE,
            reg_type: MODBUS_DEFAULT_DEVICE_REGISTER_TYPE,
            word: MODBUS_DEFAULT_DEVICE_ENDIANNESS
        };
        this.devices.push(this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameDevice(devId: string, newName: string) {
        const dev = this.devices.find(dev => dev.devId === devId);
        dev.devId = newName;
        this.cfg.devices[newName] = this.cfg.devices[devId];
        delete this.cfg.devices[devId];
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteDevice(devId: string) {
        const idx = this.devices.findIndex(dev => dev.devId === devId);
        this.devices[idx].valueSubscription.unsubscribe();
        this.devices[idx].statusSubscription.unsubscribe();
        this.devices.splice(idx, 1);
        delete this.cfg.devices[devId];
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity may change (e.g. in case an invalid device is deleted)
        this.updateValidity();
    }

    private setDevicePolicy(devId: string, policy: PollingPolicy) {
        this.cfg.devices[devId] = setDevicePolicy(this.cfg.devices[devId], policy);
        this.updateConfig();
        // `policy` is assumed to contain valid data, so no need for validity update
    }

    private setDeviceAdvancedSettings(dev: DeviceConfig,
                                      address: number,
                                      dataType: DataType,
                                      regType: RegType,
                                      word: Endianness,
                                      expression: string) {
        this.cfg.devices[dev.devId].address = address;
        this.cfg.devices[dev.devId].data_type = dataType;
        this.cfg.devices[dev.devId].reg_type = regType;
        this.cfg.devices[dev.devId].word = word;
        this.cfg.devices[dev.devId].expression = expression && expression.trim().length ? expression.trim() : null;
        this.updateConfig();
        // validity may have changed (e.g. an expression may just have been set for a multi-register device)
        dev.form.controls.register.updateValueAndValidity(); // automatically triggers a status update
    }

    private mkGatewayConfig(gatewayId: string): GatewayConfig {
        const cfg = {
            gatewayId: gatewayId,
            form: this.fb.group({
                host: new FormControl(this.cfg.gateways[gatewayId].host, [validateNotBlank]),
                port: new FormControl(this.cfg.gateways[gatewayId].port, [Validators.required, validateInteger, validateGt(0)]),
                frame: new FormControl(this.cfg.gateways[gatewayId].frame, [Validators.required])
            })
        } as GatewayConfig;
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const gateway = this.cfg.gateways[cfg.gatewayId];
            gateway.host = value['host'];
            gateway.port = value['port'];
            gateway.frame = value['frame'];
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceConfig(devId: string): DeviceConfig {
        const cfg = {
            devId: devId,
            form: this.fb.group({
                gatewayId: new FormControl(this.cfg.devices[devId].gateway, [Validators.required]),
                register: new FormControl(registerToForm(this.cfg.devices[devId].register), []),
                uom: new FormControl(this.cfg.devices[devId].uom, [])
            })
        } as DeviceConfig;
        // NOTE: it is crucial that `cfg` reference does not change at run-time, so that register validator is always able
        //       to access the device config structure, where the (potentially renamed) device id is stored
        cfg.form.controls.register.setValidators([
            validateNotBlankTrim([' ', ',']),
            validateIntegerArray(','),
            (ctrl: AbstractControl) => this.validateRegisterAgainstExpression(ctrl, cfg)
        ]);
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const dev = this.cfg.devices[cfg.devId];
            dev.gateway = value['gatewayId'] ? value['gatewayId'].trim() : null;
            dev.register = formToRegister(value['register']);
            dev.uom = value['uom'] && value['uom'].trim().length ? value['uom'].trim() : null;
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceUniquenessValidator(ignoreDevice?: string): AsyncValidatorFn {
        return ctrl => {
            const device = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            if (!device || (!!ignoreDevice && device === ignoreDevice)) {
                return of(null);
            }
            return this.isDeviceDuplicate(device).pipe(
                map(isDuplicate => isDuplicate ? {'duplicate': true} : null)
            );
        };
    }

    private mkTemplateGatewayIdValidator(templateGetter: () => ModbusTemplate): AsyncValidatorFn {
        return ctrl => {
            const gatewayId = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            const template = templateGetter();
            if (!gatewayId || !template) {
                return null;
            }
            // check for duplicates inside this very connector, using *current* values
            if (this.gateways.find(gatewayCfg => gatewayCfg.gatewayId === gatewayId)) {
                return of({'duplicate': true});
            }
            // check for device duplicates
            return forkJoin(getTemplateDeviceNames(gatewayId, template).map(device => this.isDeviceDuplicate(device))).pipe(
                map(isDuplicates => isDuplicates.includes(true) ? {duplicate_dev: true} : null)
            );
        };
    }

    private isDeviceDuplicate(device: string): Observable<boolean> {
        // check for duplicates inside this very connector, using *current* values
        if (this.devices.find(devCfg => devCfg.devId === device)) {
            return of(true);
        }
        return this.apiService.checkConnectorDeviceExistence(device).pipe(
            switchMap(resp => resp.value ?
                this.apiService.findConnectorByDevice(device).pipe(
                    map(idResp => idResp?.id !== this.id) // ignore "this very connector" (already checked against current values)
                ) :
                of(false))
        );
    }

    private validateRegisterAgainstExpression(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const reg = formToRegister(ctrl.value);
        if (_.isArray(reg) && reg.length > 1) {
            const expr = this.cfg.devices[dev.devId].expression;
            if (!expr || !expr.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private validateExpressionAgainstRegister(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const reg = this.cfg.devices[dev.devId].register;
        if (_.isArray(reg) && reg.length > 1) {
            if (!ctrl.value || !ctrl.value.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private mkPagedGws(size: number, page: number): Observable<PagedGateways> {
        let pagedGws = [];
        for (let i = 0; i < this.gateways.length; i += size) {
            pagedGws.push(this.gateways.slice(i, i + size));
        }
        return of({
            data: pagedGws[page],
            total: this.gateways.length
        } as PagedGateways);
    }

    private mkPagedDevices(size: number, page: number): Observable<PagedDevices> {
        let pagedDevs = [];
        for (let i = 0; i < this.devices.length; i += size) {
            pagedDevs.push(this.devices.slice(i, i + size));
        }
        return of({
            data: pagedDevs[page],
            total: this.devices.length
        } as PagedDevices);
    }
}

function registerToForm(register: number | number[]): string {
    if (!isDefined(register)) {
        return null;
    }
    if (_.isArray(register)) {
        return register.join(', ');
    }
    return register.toString();
}

function formToRegister(register: string): number | number[] {
    if (!register) {
        return null;
    }
    const registers = register.split(',').map(p => parseInt(p));
    return registers.length > 1 ? registers : registers[0];
}

function getTemplateDeviceNames(gatewayId: string, template: ModbusTemplate): string[] {
    return template.devices.map(dev => mkDeviceParamValue(gatewayId, dev.suffix));
}
